import React, { useEffect, useState } from "react";

interface Collab {
	name: string;
	url?: string;
}

interface Client {
	name: string;
	url?: string;
	services?: string[];
	collabs?: Collab[];
}

// Fallback content, shown until the CMS responds (and kept if it can't)
const fallbackClients: Client[] = [
	{
		name: "1906",
		url: "https://1906.shop/",
		services: [
			"Art Direction",
			"Creative Direction",
			"Marketing Collateral",
			"Packaging",
			"Production",
			"Visual Merchandising",
		],
	},
	{
		name: "ALCOVE",
		url: "https://www.reservealcove.com/",
		services: ["Branding", "Web Design", "Web Development"],
	},
	{
		name: "ALSOLIS",
		url: "https://alsolis.com/",
		services: ["Branding", "Web Design", "Web Development"],
	},
	{
		name: "Dune Suncare",
		url: "https://dunesuncare.com/",
		services: ["Marketing Collateral", "Production", "Special Projects"],
	},
	{
		name: "Eleasium",
		url: "https://eleasium.world/",
		services: ["Branding", "Web Design", "Web Development"],
	},
	{
		name: "Funner",
		url: "https://www.shopfunner.com",
		services: ["Special Projects", "Web Design"],
	},
	{
		name: "greige basics",
		url: "https://greigebasics.com/",
		services: ["Branding", "Web Design", "Web Development"],
	},
	{
		name: "Hudson Cannabis",
		url: "https://www.hudsoncannabis.co/",
		services: [
			"Art Direction",
			"Marketing Collateral",
			"Production",
			"Visual Merchandising",
			"Web Design",
			"Web Development",
		],
	},
	{
		name: "Mockly",
		url: "https://drinkmockly.com/",
		services: ["Marketing Collateral"],
	},
	{
		name: "Negative Underwear",
		url: "https://negativeunderwear.com/",
		services: ["Special Projects", "Web Design"],
	},
	{
		name: "Nili Lotan",
		url: "https://www.nililotan.com/",
		services: ["Web Design"],
	},
	{
		name: "Nori",
		url: "https://nori.co/",
		services: ["Packaging", "Visual Merchandising"],
	},
	{
		name: "Selima Optique",
		url: "https://selimaoptique.com/",
		services: ["Marketing Collateral"],
	},
	{
		name: "Soft Services",
		url: "https://softservices.com/",
		services: ["Special Projects", "Web Design", "Web Development"],
	},
	{
		name: "Stars + Honey",
		url: "https://starsandhoney.com/",
		services: ["Creative Direction", "Marketing Collateral", "Web Design"],
	},
	{
		name: "TINGE Beauty",
		url: "https://tingebeauty.com/",
		services: ["Marketing Collateral"],
	},
];

const fallbackServices = [
	"Art Direction",
	"Branding",
	"Creative Direction",
	"Digital Strategy",
	"Marketing Collateral",
	"Packaging",
	"Production",
	"Project Management",
	"Special Projects",
	"Visual Merchandising",
	"Web Design",
	"Web Development",
];

const alphabetical = (a: string, b: string) =>
	a.localeCompare(b, "en", { sensitivity: "base", numeric: true });

const SANITY_QUERY_URL =
	"https://3ti0i8bf.api.sanity.io/v2023-07-04/data/query/production?query=" +
	encodeURIComponent(
		`{
			"services": *[_type == "service"].title,
			"clients": *[_type == "client"]{name, url, "services": services[]->title, "collabs": collabs[]->{name, url}}
		}`
	);

export const StudioCopy = () => {
	const [services, setServices] = useState(fallbackServices);
	const [clients, setClients] = useState(fallbackClients);

	useEffect(() => {
		let cancelled = false;
		fetch(SANITY_QUERY_URL)
			.then((res) => res.json())
			.then(({ result }) => {
				if (cancelled || !result) return;
				if (Array.isArray(result.services) && result.services.length) {
					setServices([...result.services].sort(alphabetical));
				}
				if (Array.isArray(result.clients) && result.clients.length) {
					const sorted = [...result.clients]
						.filter((c: Client) => c && c.name)
						.sort((a: Client, b: Client) => alphabetical(a.name, b.name))
						.map((c: Client) => ({
							...c,
							services: [...(c.services ?? [])].sort(alphabetical),
							collabs: [...(c.collabs ?? [])]
								.filter((p) => p && p.name)
								.sort((a, b) => alphabetical(a.name, b.name)),
						}));
					setClients(sorted);
				}
			})
			.catch(() => {
				// Fall back to the built-in content
			});
		return () => {
			cancelled = true;
		};
	}, []);

	return (
		<React.Fragment>
			<div className="nh-headline-track">
				<p className="nh-headline">
					A <em>better</em>, <em>faster</em>, &amp; <em>nicer</em> creative
					studio.
				</p>
			</div>
			<main className="nh-main">
				<div className="nh-rest fade-in">
				<div>
					<p className="nh-label">Work</p>
					<p className="nh-client">
						<a
							href="https://www.instagram.com/wownicehandwriting/"
							target="_blank"
							rel="noreferrer"
						>
							@wownicehandwriting
						</a>
					</p>
				</div>

				<div>
					<p className="nh-label">Services</p>
					{services.map((service) => (
						<p key={service}>{service}</p>
					))}
				</div>

				<div className="nh-clients">
					<p className="nh-label">Clients</p>
					{clients.map(
						({ name, url, services: clientServices, collabs }, index) => (
						<p key={name} className="nh-client">
							<span className="nh-client-num" aria-hidden="true">
								[{String(index + 1).padStart(2, "0")}]
							</span>
							{url ? (
								<a href={url} target="_blank" rel="noreferrer">
									{name}
								</a>
							) : (
								name
							)}
							{clientServices && clientServices.length ? (
								<>
									{" "}
									<span className="nh-client-services">
										— {clientServices.join(", ")}
									</span>
								</>
							) : null}
							{collabs && collabs.length ? (
								<>
									{" "}
									<span className="nh-client-collab">
										— (collab w/{" "}
										{collabs.map((partner, i) => (
											<React.Fragment key={partner.name}>
												{partner.url ? (
													<a
														href={partner.url}
														target="_blank"
														rel="noreferrer"
													>
														{partner.name}
													</a>
												) : (
													partner.name
												)}
												{i < collabs.length - 1 ? ", " : ""}
											</React.Fragment>
										))}
										)
									</span>
								</>
							) : null}
						</p>
						)
					)}
				</div>

				<div className="nh-footer">
					<p className="nh-italic">
						New clients accepted by referral only.
					</p>
					<p>© 2024–{new Date().getFullYear()} Nice Handwriting LLC</p>
				</div>
				</div>
			</main>
		</React.Fragment>
	);
};
